import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Droplet, Calculator, Info } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './PoolVolumeCalculatorPage/components/CalculationForm';
import ResultsOverview from './PoolVolumeCalculatorPage/components/ResultsOverview';
import VisualizationSection from './PoolVolumeCalculatorPage/components/VisualizationSection';
import QuickAdjustmentSidebar from './PoolVolumeCalculatorPage/components/QuickAdjustmentSidebar';
import WaterMaintenanceSidebar from './PoolVolumeCalculatorPage/components/WaterMaintenanceSidebar';
import InfoContent from './PoolVolumeCalculatorPage/components/InfoContent';
import RecommendationsSidebar from './PoolVolumeCalculatorPage/components/RecommendationsSidebar';
import SavedCalculationsSidebar from './PoolVolumeCalculatorPage/components/SavedCalculationsSidebar';

// Определение начального состояния формы
export const initialFormData = {
  poolType: 'rectangular',
  length: '8',
  width: '4',
  depth: '1.5',
  averageDepth: '1.5',
  diameter: '4',
  radius: '2',
  a: '4',
  b: '3',
  waterLevel: '90', // процент заполнения
  stepArea: '0', // площадь ступеней/лестниц в м²
  unitSystem: 'metric', // metric или imperial
  wallThickness: '0.3', // толщина стенок бассейна в метрах
  bottomSlope: 'flat', // flat, gradual, steep
  specificFeatures: [], // ['stairs', 'ladder', 'beach_entry', 'spa']
  maintenanceLevel: 'standard', // basic, standard, premium
  waterType: 'chlorine', // хлор, соль, etc.
  climate: 'moderate', // холодный, умеренный, жаркий
  poolLocation: 'outdoor', // indoor или outdoor
  seasonLength: '3', // месяцев в году
  coverType: 'none', // 'none', 'standard', 'solar'
  heatingSystem: 'none', // 'none', 'electric', 'solar', 'heat_pump'
  filterSystem: 'sand', // 'sand', 'cartridge', 'de'
};

// Определение типов бассейнов
export const poolTypes = [
  { id: 'rectangular', name: 'Прямоугольный', icon: 'Square' },
  { id: 'circular', name: 'Круглый', icon: 'Circle' },
  { id: 'oval', name: 'Овальный', icon: 'Oval' },
  { id: 'irregular', name: 'Нестандартной формы', icon: 'Maximize' },
  { id: 'custom', name: 'Произвольной формы', icon: 'Pencil' }
];

// Определение типов дна бассейна
export const bottomSlopeTypes = [
  { id: 'flat', name: 'Плоское дно', description: 'Одинаковая глубина по всему бассейну' },
  { id: 'gradual', name: 'Плавный уклон', description: 'Постепенное увеличение глубины' },
  { id: 'steep', name: 'Резкий уклон', description: 'Быстрое увеличение глубины с пологой зоной' },
];

// Дополнительные элементы бассейна
export const specificFeatures = [
  { id: 'stairs', name: 'Лестница', volume: 0.5 }, // средний объем в м³
  { id: 'ladder', name: 'Лесенка', volume: 0.1 },
  { id: 'beach_entry', name: 'Пляжный вход', volume: 1.2 },
  { id: 'spa', name: 'Спа-зона', volume: 1.5 },
  { id: 'kids_zone', name: 'Детская зона', volume: 0.8 }
];

// Различные системы очистки воды
export const waterTypes = [
  { id: 'chlorine', name: 'Хлор', maintenanceCost: 1.0 },
  { id: 'salt', name: 'Соляная система', maintenanceCost: 0.8 },
  { id: 'bromine', name: 'Бром', maintenanceCost: 1.2 },
  { id: 'ozone', name: 'Озонирование', maintenanceCost: 0.7 },
  { id: 'uv', name: 'УФ-очистка', maintenanceCost: 0.6 }
];

// Фильтрационные системы
export const filterSystems = [
  { id: 'sand', name: 'Песочный фильтр', maintenanceFactor: 1.0, replacementYears: 5 },
  { id: 'cartridge', name: 'Картриджный фильтр', maintenanceFactor: 1.2, replacementYears: 2 },
  { id: 'de', name: 'Диатомовый фильтр', maintenanceFactor: 1.5, replacementYears: 7 }
];

/**
 * PoolVolumeCalculatorPage Component - Калькулятор объема бассейна с расчетом затрат на обслуживание и расхода воды.
 */
const PoolVolumeCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('pool-volume-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор объема бассейна | Gensio';
  const metaDescription = tool?.metaDescription || 'Онлайн калькулятор объема бассейна: точный расчет объема воды для бассейнов любой формы (прямоугольный, круглый, овальный) с учетом особенностей конструкции и расчетом стоимости обслуживания.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [activeVisualTab, setActiveVisualTab] = useState('3d');

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedData = localStorage.getItem('savedPoolCalculations');
    if (savedData) {
      try {
        setSavedCalculations(JSON.parse(savedData));
      } catch (e) {
        console.error("Ошибка загрузки сохраненных расчетов:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'rectangular':
          newState.poolType = 'rectangular';
          break;
        case 'circular':
          newState.poolType = 'circular';
          break;
        case 'oval':
          newState.poolType = 'oval';
          break;
        case 'irregular':
          newState.poolType = 'irregular';
          break;
        case 'indoor':
          newState.poolLocation = 'indoor';
          break;
        case 'outdoor':
          newState.poolLocation = 'outdoor';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalculation = {
      id: Date.now().toString(),
      name: name || `Бассейн ${formatPoolType(formData.poolType)}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedCalculations = [...savedCalculations, newSavedCalculation];
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedPoolCalculations', JSON.stringify(updatedSavedCalculations));

    return newSavedCalculation;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalculations = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedPoolCalculations', JSON.stringify(updatedSavedCalculations));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calcToLoad = savedCalculations.find(calc => calc.id === id);
    if (calcToLoad) {
      setFormData(calcToLoad.formData);
      setResults(calcToLoad.results);
    }
  };

  // Форматирование типа бассейна для отображения
  const formatPoolType = (type) => {
    const poolType = poolTypes.find(pt => pt.id === type);
    return poolType ? poolType.name.toLowerCase() : 'бассейн';
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.sky.100)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.sky.900)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Droplet className="w-8 h-8 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор объема бассейна'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              poolTypes={poolTypes}
              bottomSlopeTypes={bottomSlopeTypes}
              specificFeatures={specificFeatures}
              waterTypes={waterTypes}
              filterSystems={filterSystems}
              saveCalculation={saveCalculation}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} formData={formData} poolTypes={poolTypes} />
                <VisualizationSection 
                  formData={formData}
                  results={results}
                  activeTab={activeVisualTab}
                  setActiveTab={setActiveVisualTab}
                />
              </>
            )}

            {/* --- Информационный контент о бассейнах --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              poolTypes={poolTypes}
            />

            {/* --- Панель сохраненных расчетов --- */}
            <SavedCalculationsSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
            />

            {/* --- Панель рекомендаций по обслуживанию --- */}
            <WaterMaintenanceSidebar 
              formData={formData}
              results={results}
              waterTypes={waterTypes}
              filterSystems={filterSystems}
            />

            {/* --- Рекомендуемое оборудование --- */}
            <RecommendationsSidebar 
              formData={formData}
              results={results}
            />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Water Animation */
        .water-ripple {
          background: linear-gradient(to bottom, rgba(44, 180, 255, 0.2), rgba(0, 128, 255, 0.6));
          position: relative;
          overflow: hidden;
        }
        .water-ripple::before,
        .water-ripple::after {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: radial-gradient(circle at center, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 60%);
          animation: ripple 10s infinite linear;
          transform-origin: center bottom;
        }
        .water-ripple::after {
          animation-delay: -5s;
          animation-duration: 15s;
        }
        @keyframes ripple {
          0% {
            transform: scale(1);
            opacity: 0;
          }
          20% {
            opacity: 0.5;
          }
          100% {
            transform: scale(3);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default PoolVolumeCalculatorPage;